import React, { useState } from 'react';
import { Brain, Info, HeartHandshake } from 'lucide-react';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Auto-questionnaire ORIGINAL et générique de repérage du stress au quotidien.
// Ce n'est PAS une échelle clinique validée (comme la HAD ou le PSS, protégées) :
// les questions ci-dessous sont rédigées librement, à titre indicatif seulement,
// et ne posent aucun diagnostic (ni de stress, ni de burnout, ni de dépression).
const QUESTION_KEYS = ['stress_q1', 'stress_q2', 'stress_q3', 'stress_q4', 'stress_q5', 'stress_q6', 'stress_q7', 'stress_q8'];

const OPTION_KEYS = [
  { value: 0, labelKey: 'stress_opt_never' },
  { value: 1, labelKey: 'stress_opt_sometimes' },
  { value: 2, labelKey: 'stress_opt_often' },
  { value: 3, labelKey: 'stress_opt_almost_always' },
];

const StressSelfCheck = () => {
  const { t } = useLanguage();
  const QUESTIONS = QUESTION_KEYS.map((k) => t(k));
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const allAnswered = QUESTIONS.every((_, i) => answers[i] !== undefined);
  const total = Object.values(answers).reduce((sum, v) => sum + v, 0);
  const maxScore = QUESTIONS.length * 3;

  let band = null;
  if (showResult) {
    const ratio = total / maxScore;
    if (ratio < 0.3) band = { labelKey: 'stress_band_low_label', color: '#10B981', textKey: 'stress_band_low_text' };
    else if (ratio < 0.6) band = { labelKey: 'stress_band_mid_label', color: '#F59E0B', textKey: 'stress_band_mid_text' };
    else band = { labelKey: 'stress_band_high_label', color: '#EF4444', textKey: 'stress_band_high_text' };
  }

  return (
    <ToolPageLayout
      title={t('stress_title')}
      subtitle={t('stress_subtitle')}
      specialty="generaliste"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <Info className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('stress_info')}
        </p>
      </div>

      <p className="text-sm text-[#64748B] mb-4">{t('stress_intro')}</p>

      <div className="space-y-5 mb-8">
        {QUESTIONS.map((q, i) => (
          <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
            <p className="text-sm text-[#0F172A] mb-3">{q}</p>
            <div className="flex flex-wrap gap-2">
              {OPTION_KEYS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { setAnswers({ ...answers, [i]: o.value }); setShowResult(false); }}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${answers[i] === o.value ? 'bg-[#0596DE] text-white border-[#0596DE]' : 'bg-[#F8FAFC] text-[#0F172A] border-[#E2E8F0] hover:border-[#0596DE]/40'}`}
                >
                  {t(o.labelKey)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {!showResult && (
        <button
          type="button"
          disabled={!allAnswered}
          onClick={() => setShowResult(true)}
          className="bg-[#0596DE] text-white font-medium text-sm px-5 py-2.5 rounded-xl hover:bg-[#047CBA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t('stress_see_result_btn')}
        </button>
      )}

      {showResult && band && (
        <div className="rounded-2xl p-6 border" style={{ borderColor: `${band.color}40`, backgroundColor: `${band.color}0D` }}>
          <div className="flex items-center gap-2 mb-2">
            <Brain className="w-5 h-5" style={{ color: band.color }} />
            <span className="font-bold text-lg" style={{ color: band.color }}>{t(band.labelKey)}</span>
          </div>
          <p className="text-sm text-[#0F172A]">{t(band.textKey)}</p>
        </div>
      )}

      <div className="mt-8 bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-4 flex gap-3">
        <HeartHandshake className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('stress_help_note')}
        </p>
      </div>
    </ToolPageLayout>
  );
};

export default StressSelfCheck;
